import React, { createContext, useContext, ReactNode } from 'react';

export interface HelpContent {
  title: string;
  description: string;
  steps?: string[];
  tips?: string[];
  examples?: string[];
  warnings?: string[];
}

interface HelpContextType {
  getHelpContent: (key: string) => HelpContent | null;
  helpContents: Record<string, HelpContent>;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

// Predefined help content for common features
const defaultHelpContents: Record<string, HelpContent> = {
  'task-creation': {
    title: 'Creating a New Task',
    description: 'Learn how to create a new task with all necessary information for effective project management.',
    steps: [
      'Click the "Create Task" button in the Task Management section',
      'Fill in the required fields: Title, Description, Priority, Release Topic',
      'Set optional fields: Assignee, Estimated Hours, Due Date, Tags',
      'Review all information before submitting',
      'Click "Create Task" to save and create the task'
    ],
    tips: [
      'Use descriptive titles that clearly indicate the work required',
      'Set realistic estimated hours to help with project planning',
      'Choose the appropriate priority level based on business impact',
      'Add detailed descriptions to help assignees understand requirements',
      'Use tags to categorize tasks for better organization'
    ],
    examples: [
      'Title: "Implement User Authentication System"',
      'Description: "Add OAuth2 authentication with Google and GitHub providers, including user registration and login flows"',
      'Priority: HIGH',
      'Estimated Hours: 16',
      'Tags: ["authentication", "security", "frontend"]'
    ],
    warnings: [
      'Task titles should be specific and actionable',
      'Estimated hours should be realistic based on team capabilities',
      'High priority tasks should be limited to critical business needs'
    ]
  },

  'task-search': {
    title: 'Searching and Filtering Tasks',
    description: 'Use advanced search and filtering capabilities to quickly find and organize tasks.',
    steps: [
      'Use the search bar to find tasks by title or description',
      'Apply status filters to focus on specific workflow stages',
      'Filter by priority to identify high-impact work',
      'Filter by release topic to focus on specific project areas',
      'Combine multiple filters for precise results',
      'Clear filters to reset the view'
    ],
    tips: [
      'Use specific keywords in search for better results',
      'Combine search terms with filters for precise targeting',
      'Save common filter combinations for quick access',
      'Regularly clear filters to avoid missing important tasks',
      'Use status filters to focus on actionable work'
    ],
    examples: [
      'Search: "authentication" to find all auth-related tasks',
      'Status Filter: "IN_PROGRESS" to see active tasks',
      'Priority Filter: "HIGH, URGENT" for critical tasks',
      'Release Topic: "User Management" for specific module tasks'
    ]
  },

  'task-status': {
    title: 'Managing Task Status',
    description: 'Track task progress through different statuses and manage workflow transitions effectively.',
    steps: [
      'Click "Change Status" button in task details',
      'Select the appropriate new status from the dropdown',
      'Review the status change before confirming',
      'Click "Update Status" to apply the change',
      'Monitor status changes in the task list'
    ],
    tips: [
      'Update status regularly to keep the team informed',
      'Use BLOCKED status when waiting for dependencies',
      'Move tasks to IN_REVIEW when ready for feedback',
      'Mark as COMPLETED only after thorough review',
      'Use status changes to trigger notifications and updates'
    ],
    examples: [
      'TODO → IN_PROGRESS: When development work begins',
      'IN_PROGRESS → IN_REVIEW: When code is ready for review',
      'IN_REVIEW → COMPLETED: When review is approved',
      'IN_PROGRESS → BLOCKED: When waiting for API changes'
    ],
    warnings: [
      'Status changes should reflect actual progress, not desired progress',
      'Avoid skipping statuses unless absolutely necessary',
      'BLOCKED tasks should include comments explaining the blocker'
    ]
  },

  'task-editing': {
    title: 'Editing Task Details',
    description: 'Modify task information to keep it current and accurate throughout the project lifecycle.',
    steps: [
      'Click the "View Details" icon to open task details',
      'Click the "Edit" button to enable editing mode',
      'Modify the fields that need to be updated',
      'Review changes before saving',
      'Click "Save Changes" to apply updates',
      'Use "Cancel" to discard changes'
    ],
    tips: [
      'Only edit tasks you have permission to modify',
      'Update estimated hours based on actual progress',
      'Keep descriptions current with latest requirements',
      'Update assignees when responsibilities change',
      'Add comments to explain significant changes'
    ],
    examples: [
      'Update estimated hours from 8h to 12h based on complexity',
      'Change assignee from "John" to "Sarah" due to workload',
      'Update description with additional requirements',
      'Modify due date to reflect new timeline'
    ],
    warnings: [
      'Major changes should be communicated to the team',
      'Avoid changing task scope without proper approval',
      'Keep audit trail of significant modifications'
    ]
  },

  'task-comments': {
    title: 'Adding Comments to Tasks',
    description: 'Use comments to provide context, updates, and facilitate team collaboration on tasks.',
    steps: [
      'Navigate to the Comments tab in task details',
      'Type your comment in the text area provided',
      'Review your comment for clarity and accuracy',
      'Click "Add Comment" to post your message',
      'View all comments with timestamps and authors'
    ],
    tips: [
      'Use comments to provide progress updates',
      'Ask questions and request clarifications',
      'Share relevant information and context',
      'Keep comments concise but informative',
      'Use @mentions to notify specific team members'
    ],
    examples: [
      'Comment: "Started implementing OAuth2 flow, will complete by EOD"',
      'Comment: "Need clarification on password requirements - @product-manager"',
      'Comment: "Code review completed, ready for testing phase"',
      'Comment: "Blocked by API changes, waiting for backend team"'
    ],
    warnings: [
      'Comments are visible to all team members',
      'Avoid sharing sensitive information in comments',
      'Keep comments professional and constructive'
    ]
  },

  'task-attachments': {
    title: 'Managing Task Attachments',
    description: 'Attach relevant files to tasks for documentation, reference, and collaboration.',
    steps: [
      'Navigate to the Attachments tab in task details',
      'Click "Upload File" to add new attachments',
      'Select files from your device',
      'Review file information before uploading',
      'View and download attached files as needed',
      'Delete attachments that are no longer relevant'
    ],
    tips: [
      'Attach design mockups for UI-related tasks',
      'Upload technical specifications and requirements',
      'Add test results and bug reports',
      'Include relevant documentation and references',
      'Keep file sizes reasonable for better performance'
    ],
    examples: [
      'Attach: "login-page-design.figma" for UI task',
      'Attach: "api-specification.pdf" for backend task',
      'Attach: "test-results.xlsx" for testing task',
      'Attach: "requirements-doc.docx" for planning task'
    ],
    warnings: [
      'Only upload files relevant to the task',
      'Ensure files don\'t contain sensitive information',
      'Regularly clean up outdated attachments',
      'Check file size limits before uploading'
    ]
  },

  'task-dashboard': {
    title: 'Using the Task Dashboard',
    description: 'Monitor project progress, view statistics, and track key metrics for better project management.',
    steps: [
      'Access the Task Dashboard from the main navigation',
      'Review total tasks count and completion status',
      'Check tasks by priority distribution',
      'Monitor overdue tasks and time tracking',
      'Use progress indicators to assess project health',
      'Export data for reporting if needed'
    ],
    tips: [
      'Review dashboard metrics regularly to identify bottlenecks',
      'Use progress tracking to adjust project timelines',
      'Monitor overdue tasks to prevent project delays',
      'Check time tracking accuracy for better future estimates',
      'Share dashboard insights with stakeholders'
    ],
    examples: [
      'Total Tasks: 25, Completed: 18, In Progress: 5',
      'Priority Distribution: HIGH (8), MEDIUM (12), LOW (5)',
      'Overdue Tasks: 2 tasks past due date',
      'Progress: 72% complete based on task status'
    ],
    warnings: [
      'Dashboard data is only as accurate as task updates',
      'Regular status updates are essential for accurate metrics',
      'Don\'t rely solely on dashboard for project decisions'
    ]
  },

  'task-priority': {
    title: 'Setting Task Priorities',
    description: 'Effectively prioritize tasks to ensure important work gets attention and resources.',
    steps: [
      'Set priority when creating or editing tasks',
      'Choose from: LOW, MEDIUM, HIGH, URGENT',
      'Consider business impact and urgency',
      'Review and adjust priorities as needed',
      'Use priority filters to focus on high-impact work'
    ],
    tips: [
      'Use URGENT only for critical, time-sensitive items',
      'HIGH priority for important but not urgent tasks',
      'MEDIUM for standard priority work',
      'LOW for nice-to-have features and improvements',
      'Regularly review and rebalance priorities'
    ],
    examples: [
      'URGENT: Security vulnerabilities, production bugs',
      'HIGH: Core features, user-facing improvements',
      'MEDIUM: Enhancements, optimizations, refactoring',
      'LOW: Documentation, minor UI tweaks, nice-to-have features'
    ],
    warnings: [
      'Too many high-priority tasks can reduce effectiveness',
      'Priorities should align with business objectives',
      'Regular priority reviews prevent priority inflation'
    ]
  },

  'task-time-tracking': {
    title: 'Time Tracking for Tasks',
    description: 'Track estimated vs actual time to improve project planning and resource allocation.',
    steps: [
      'Set estimated hours when creating tasks',
      'Update actual hours as work progresses',
      'Monitor progress through the dashboard',
      'Compare estimates vs actuals for future planning',
      'Use time data to improve future estimates'
    ],
    tips: [
      'Set realistic estimates based on team capabilities',
      'Update actual hours regularly for accurate tracking',
      'Use historical data to improve future estimates',
      'Consider complexity and dependencies in estimates',
      'Track time spent on different task phases'
    ],
    examples: [
      'Estimated: 8h, Actual: 6h (Under estimate)',
      'Estimated: 4h, Actual: 8h (Over estimate)',
      'Progress: 75% complete based on time tracking',
      'Average accuracy: 85% across all tasks'
    ],
    warnings: [
      'Time tracking should be honest and accurate',
      'Don\'t inflate hours to meet estimates',
      'Use time data to improve processes, not just estimates'
    ]
  }
};

interface HelpProviderProps {
  children: ReactNode;
  customHelpContents?: Record<string, HelpContent>;
}

export function HelpProvider({ children, customHelpContents = {} }: HelpProviderProps) {
  const helpContents = {
    ...defaultHelpContents,
    ...customHelpContents
  };

  const getHelpContent = (key: string): HelpContent | null => {
    return helpContents[key] || null;
  };

  const value: HelpContextType = {
    getHelpContent,
    helpContents
  };

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
}

export function useHelp() {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
}

export function useHelpContent(key: string) {
  const { getHelpContent } = useHelp();
  return getHelpContent(key);
}
